import 'leaflet/dist/leaflet.css';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { svg, type LatLngTuple } from 'leaflet';
import { AttributionControl, MapContainer, Polygon, ZoomControl, useMap } from 'react-leaflet';
import { cn } from '@/lib/utils';
import { COUNTRY_BOUNDS, DIVISIONS } from './bangladeshGeometry';
import {
  MAP_COUNTRY_FIT_PADDING,
  MAP_LARGE_LABEL_MAX_ZOOM,
  MAP_MAX_TILE_ZOOM,
  MAP_MIN_ZOOM,
  MAP_PAN_LIMIT_BOUNDS,
  MAP_SATELLITE_ATTRIBUTION,
  MAP_SATELLITE_LABELS_TILE_URL,
  MAP_SATELLITE_TILE_URL,
  MAP_STREET_ATTRIBUTION,
  MAP_STREET_TILE_URL,
} from './map.constants';
import { SharpTileLayer } from './SharpTileLayer';
import { SmoothWheelZoom } from './SmoothWheelZoom';

const MASK_RENDERER_PADDING = 2;
const ZOOM_SNAP_STEP = 0.1;
const PAN_LIMIT_VISCOSITY = 0.8;
const DIVISION_BORDER_WEIGHT = 1;
const DIVISION_BORDER_DASH = '4 4';

const WORLD_RING: LatLngTuple[] = [
  [-85, -180],
  [-85, 180],
  [85, 180],
  [85, -180],
];

const MASK_RINGS: LatLngTuple[][] = [
  WORLD_RING,
  ...DIVISIONS.flatMap((division) => division.rings),
];

function CountryOverlay() {
  const renderer = useMemo(() => svg({ padding: MASK_RENDERER_PADDING }), []);

  return (
    <>
      <Polygon
        positions={MASK_RINGS}
        interactive={false}
        renderer={renderer}
        className="fill-muted"
        pathOptions={{ stroke: false, fillOpacity: 1 }}
      />
      {DIVISIONS.map((division) => (
        <Polygon
          key={division.name}
          positions={division.rings}
          interactive={false}
          renderer={renderer}
          className="stroke-muted-foreground/50"
          pathOptions={{
            fill: false,
            weight: DIVISION_BORDER_WEIGHT,
            dashArray: DIVISION_BORDER_DASH,
          }}
        />
      ))}
    </>
  );
}

function ResizeWithContainer() {
  const map = useMap();

  useEffect(() => {
    const observer = new ResizeObserver(() => map.invalidateSize({ debounceMoveend: true }));
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map]);

  return null;
}

function useHasBeenSized() {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [hasBeenSized, setHasBeenSized] = useState(false);

  useEffect(() => {
    if (!element || hasBeenSized) return;
    const observer = new ResizeObserver(() => {
      if (element.clientWidth > 0 && element.clientHeight > 0) setHasBeenSized(true);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, hasBeenSized]);

  return { ref: setElement, hasBeenSized };
}

interface BangladeshMapProps {
  isSatellite: boolean;
  hasLargeLabels?: boolean;
  children?: ReactNode;
}

export function BangladeshMap({
  isSatellite,
  hasLargeLabels = false,
  children,
}: BangladeshMapProps) {
  const { ref, hasBeenSized } = useHasBeenSized();
  const maxZoom = hasLargeLabels ? MAP_LARGE_LABEL_MAX_ZOOM : MAP_MAX_TILE_ZOOM;

  return (
    <div ref={ref} className="size-full">
      {hasBeenSized && (
        <MapContainer
          bounds={COUNTRY_BOUNDS}
          boundsOptions={{ padding: MAP_COUNTRY_FIT_PADDING }}
          minZoom={MAP_MIN_ZOOM}
          zoomSnap={ZOOM_SNAP_STEP}
          maxBounds={MAP_PAN_LIMIT_BOUNDS}
          maxBoundsViscosity={PAN_LIMIT_VISCOSITY}
          zoomControl={false}
          attributionControl={false}
          className={cn(
            'isolate size-full bg-muted! font-sans!',
            '[&_.leaflet-control-attribution]:rounded-tl-md [&_.leaflet-control-attribution]:text-3xs!',
            '[&_.leaflet-control-zoom]:border! [&_.leaflet-control-zoom]:shadow-sm!',
            'max-lg:[&_.leaflet-control-zoom_a]:size-6! max-lg:[&_.leaflet-control-zoom_a]:text-base! max-lg:[&_.leaflet-control-zoom_a]:leading-6!',
          )}
        >
          {isSatellite ? (
            <>
              <SharpTileLayer
                key="satellite"
                url={MAP_SATELLITE_TILE_URL}
                attribution={MAP_SATELLITE_ATTRIBUTION}
                bounds={MAP_PAN_LIMIT_BOUNDS}
                maxZoom={maxZoom}
              />
              <SharpTileLayer
                key="satellite-labels"
                url={MAP_SATELLITE_LABELS_TILE_URL}
                bounds={MAP_PAN_LIMIT_BOUNDS}
                maxZoom={maxZoom}
                hasLargeLabels={hasLargeLabels}
              />
            </>
          ) : (
            <SharpTileLayer
              key="street"
              url={MAP_STREET_TILE_URL}
              attribution={MAP_STREET_ATTRIBUTION}
              bounds={MAP_PAN_LIMIT_BOUNDS}
              maxZoom={maxZoom}
              hasLargeLabels={hasLargeLabels}
            />
          )}
          <CountryOverlay />
          {children}
          <ResizeWithContainer />
          <SmoothWheelZoom />
          <AttributionControl position="bottomright" prefix={false} />
          <ZoomControl position="bottomright" />
        </MapContainer>
      )}
    </div>
  );
}
